import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { DomainResponse } from '../../components/org-admin-client/org-model/domain-response.model';
import { UserResponse } from '../../components/org-admin-client/org-model/user-response.model';
import { SessionListResponse } from '../../components/org-admin-client/org-model/session-list-response.model';
import { DeviceListResponse } from '../../components/org-admin-client/org-model/device-list-response.model';
import { SysNotificationDto } from '../../components/org-admin-client/org-model/sys-notification-dto.interface';
import { SysNotificationResponse } from '../../components/org-admin-client/org-model/sys-notification-response.model';
import { map, Observable } from 'rxjs';
import { EventReportsResponse } from '../../components/org-admin-client/org-model/event-reports-response';
import { EventReportDetailResponse } from '../../components/org-admin-client/org-model/event-report-detail-response';
import { DeleteResponse } from '../../components/org-admin-client/org-model/delete-response';
import { DeleteAllDevicesResult, deleteDevicesSequentially } from '../../../shared-api/deleteAllDevices';
import { userDevicePath, userDevicesPath } from '../../../shared-api/orgAdminDevicePaths';
import { UserListAntwort } from '../../components/org-admin-client/org-model/user-list-antwort';
import { DeletionPeriodResponse } from '../../components/org-admin-client/org-model/deletion-period-response';
import { MessengerServiceResponse } from '../../components/org-admin-client/org-model/messenger-service-response';
import { AppConfigService } from '../config/app-config.service';
import { DomainDto } from '../../components/org-admin-client/org-model/domain-dto.interface';
import { UserOidcList } from '../../components/org-admin-client/org-model/user-oidc-list';
import { CORS_WORKAROUND_TOKEN } from '../../interceptors/error/error.interceptor';

@Injectable({
  providedIn: 'root'
})
/**
 *  Hierdurch erfüllte AFOs:
 *
 * A_25483-01 - TI-M Client User-Agent
 *
 * **SiGU: TLS wird vom Browser ausgeführt und ist daher nicht übern einen HTTPClient, wie dem
 * von Angular, validierbar. Unter diesen Aspekt fallen folgende AFOs:**
 * - GS-A_5542 - TLS-Verbindungen (fatal Alert bei Abbrüchen)
 * - GS-A_5581 - 'TUC vereinfachte Zertifikatsprüfung' (Komponenten-PKI)
 * - GS-A_4387 - TLS-Verbindungen, nicht Version 1.0
 * - A_18464 - TLS-Verbindungen, nicht Version 1.1
 * - GS-A_4385 - TLS-Verbindungen, Version 1.2
 * - A_18467 - TLS-Verbindungen, Version 1.3
 * - A_21275-01 - TLS-Verbindungen, zulässige Hashfunktionen bei Signaturen im TLS-Handshake
 * - GS-A_4359-02 - X.509-Identitäten für die Durchführung einer TLS-Authentifizierung
 * - GS-A_5035 - Nichtverwendung des SSL-Protokolls
 * - GS-A_5322 - Weitere Vorgaben für TLS-Verbindungen
 * - GS-A_5526 - TLS-Renegotiation-Indication-Extension
 * - A_17124-03 - TLS-Verbindungen (ECC-Migration)
 *
 */
export class OrgAdminEndpointsService {
  private readonly http = inject(HttpClient);
  private readonly config: AppConfigService = inject(AppConfigService);
  private readonly regDienstUrl = this.config.get('regDienstUrl');
  private readonly orgAdminEndpoint = this.regDienstUrl + '/org-admin/v1/domains/';

  domainList(): Observable<DomainDto[]> {
    return this.http
      .get<DomainResponse>(this.orgAdminEndpoint)
      .pipe(map(resp => resp.result.toSorted((a, b) => a.domain.localeCompare(b.domain))));
  }

  getUsers(
    domain: string,
    paramMap: Record<string, string | number | boolean | undefined>
  ): Observable<UserListAntwort> {
    let params = new HttpParams().set('admins', false);
    if (paramMap) {
      for (const [key, value] of Object.entries(paramMap)) {
        if (value !== undefined && value !== '') {
          params = params.append(key, value);
        }
      }
    }
    return this.http.get<UserListAntwort>(this.orgAdminEndpoint + domain + '/users', { params });
  }

  getOidcUsers(
    domain: string,
    paramMap: Record<string, string | number | boolean | undefined>
  ): Observable<UserOidcList> {
    let params = new HttpParams();

    if (paramMap) {
      for (const [key, value] of Object.entries(paramMap)) {
        if (value !== undefined && value !== '') {
          params = params.append(key, value);
        }
      }
    }

    return this.http.get<UserOidcList>(this.orgAdminEndpoint + domain + '/oidc-users', {
      params
    });
  }

  resetOidcUserPassword(
    domain: string,
    body: { userId: string | undefined; username: string | undefined; initialPassword: string }
  ): Observable<void> {
    const url = `${this.orgAdminEndpoint}${domain}/oidc-users/reset-password`;
    return this.http.put<void>(url, body);
  }

  addOidcUser(
    domain: string,
    body: { username: string; initialPassword: string; firstname: string; lastname: string; emailAddress: string }
  ): Observable<void> {
    const url = `${this.orgAdminEndpoint}${domain}/oidc-users`;
    return this.http.post<void>(url, body);
  }

  getUser(domain: string, userId: string): Observable<UserResponse> {
    return this.http.get<UserResponse>(this.orgAdminEndpoint + domain + '/users/' + userId);
  }

  deleteUser(domain: string, userId: string): Observable<DeleteResponse> {
    return this.http.delete<DeleteResponse>(this.orgAdminEndpoint + domain + '/users/' + userId);
  }

  getSessionsFromUser(domain: string, userId: string): Observable<SessionListResponse> {
    return this.http.get<SessionListResponse>(this.orgAdminEndpoint + domain + '/users/' + userId + '/sessions');
  }

  getDevicesFromUser(domain: string, userId: string): Observable<DeviceListResponse> {
    return this.http.get<DeviceListResponse>(userDevicesPath(this.regDienstUrl, domain, userId));
  }

  deleteDevice(domain: string, userId: string, deviceId: string): Observable<DeleteResponse> {
    return this.http.delete<DeleteResponse>(userDevicePath(this.regDienstUrl, domain, userId, deviceId));
  }

  /**
   * Löscht mehrere Devices nacheinander und liefert eine Bilanz zurück.
   * Ein fehlgeschlagenes Device bricht den Lauf nicht ab; seine ID wird in
   * {@link DeleteAllDevicesResult.failedIds} gesammelt. Bewusst UI-frei
   * (kein Snackbar/Loading) – das Feedback übernimmt die aufrufende Komponente.
   */
  deleteAllDevices(domain: string, userId: string, deviceIds: string[]): Observable<DeleteAllDevicesResult> {
    return deleteDevicesSequentially(deviceIds, deviceId => this.deleteDevice(domain, userId, deviceId));
  }

  changeUserDisplayname(domain: string, userId: string, displayname: string): Observable<UserResponse> {
    const body = { displayname: displayname.trim() };
    if (!body.displayname) {
      throw new Error('Displayname must not be empty');
    }
    return this.http.put<UserResponse>(`${this.orgAdminEndpoint}${domain}/users/${userId}`, body, {
      context: new HttpContext().set(CORS_WORKAROUND_TOKEN, true)
    });
  }

  sendSysNotification(domain: string, notification: SysNotificationDto): Observable<SysNotificationResponse> {
    return this.http.post<SysNotificationResponse>(this.orgAdminEndpoint + domain + '/notification', notification);
  }

  getEventReports(
    domain: string,
    paramMap: Record<string, string | number | undefined>
  ): Observable<EventReportsResponse> {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(paramMap)) {
      if (value) {
        params = params.append(key, value);
      }
    }

    return this.http.get<EventReportsResponse>(this.orgAdminEndpoint + domain + '/event_reports', {
      params
    });
  }

  getEventReportsDetails(domain: string, reportId: string): Observable<EventReportDetailResponse> {
    return this.http.get<EventReportDetailResponse>(this.orgAdminEndpoint + domain + '/event_reports/' + reportId);
  }

  /**
   * Löscht den Report aus der Übersicht des Org-Admins
   */
  deleteEventReportsDetails(domain: string, reportId: string): Observable<DeleteResponse> {
    return this.http.delete<DeleteResponse>(this.orgAdminEndpoint + domain + '/event_reports/' + reportId);
  }

  getVZDToken(): Observable<string> {
    return this.http.get<string>(this.regDienstUrl + '/regservicetoken');
  }

  getRoomDeletionPeriod(domain: string): Observable<number> {
    return this.http
      .get<DeletionPeriodResponse>(this.regDienstUrl + '/client/' + domain + '/loeschfrist')
      .pipe(map(resp => resp.loeschFrist));
  }

  getMessengerService(domain: string): Observable<MessengerServiceResponse> {
    return this.http.get<MessengerServiceResponse>(this.orgAdminEndpoint + domain);
  }

  patchDeletionPeriod(durationInSec: number, domain: string): Observable<MessengerServiceResponse> {
    return this.http.patch<MessengerServiceResponse>(this.orgAdminEndpoint + domain, durationInSec);
  }
}
